// frontend/src/LeanLineups.js
//
// Independent, "excel sheet" style lineup builder. Deliberately drops
// availability/sides/squad filtering/publishing polish from CoachLineups.js
// in favor of a single dense grid: boats as columns, seats as rows, with a
// searchable athlete list down the side. Uses the same backend endpoints as
// CoachLineups.js (no backend changes).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./api";
import { FaChevronLeft, FaFilter } from "react-icons/fa";

const SEAT_TEMPLATES = {
  eight: ["C", "8", "7", "6", "5", "4", "3", "2", "1"],
  four: ["C", "4", "3", "2", "1"],
  pair: ["C", "2", "1"],
};

const BOAT_TYPE_LABEL = { eight: "8+", four: "4+", pair: "2-" };

function seatsFor(type) {
  return SEAT_TEMPLATES[String(type || "").toLowerCase()] || SEAT_TEMPLATES.pair;
}

function toLocalYmd(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function todayLocalYmd() {
  return toLocalYmd(new Date());
}

export default function LeanLineups({ coach, date: dateProp }) {
  const canEdit =
    coach?.permissions?.includes?.("lineups:edit") ?? coach?.can_edit_lineup ?? false;

  const [internalDate] = useState(() => todayLocalYmd());
  const date = dateProp || internalDate;
  const [practices, setPractices] = useState([]);
  const [practiceId, setPracticeId] = useState(null);
  const [roster, setRoster] = useState([]);
  const [boats, setBoats] = useState([]);
  const [boatConfig, setBoatConfig] = useState({ hulls: [], oars: [], coaches: [] });
  const [positionColors, setPositionColors] = useState({});
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [picker, setPicker] = useState(null); // { boatId, seat }
  const [addType, setAddType] = useState("eight");
  const [selectedAthleteId, setSelectedAthleteId] = useState(null); // tap-to-place, for touch devices
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [athletePanelCollapsed, setAthletePanelCollapsed] = useState(true);
  const [attendanceByUser, setAttendanceByUser] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [squadFilter, setSquadFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [availFilter, setAvailFilter] = useState("all");

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setIsTouchDevice(Boolean(mq.matches));
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const [teamFilter, setTeamFilter] = useState(() => String(coach?.team || "").trim());
  const coachTeam = teamFilter;

  const loadPractices = useCallback(async (targetDate) => {
    try {
      const data = await apiFetch(
        `/practices/range?from=${targetDate}&to=${targetDate}${coachTeam ? `&team=${encodeURIComponent(coachTeam)}` : ""}`
      );
      const list = Array.isArray(data) ? data : Array.isArray(data?.practices) ? data.practices : [];
      setPractices(list);
      setPracticeId((prev) => {
        if (prev && list.some((p) => String(p.id) === String(prev))) return prev;
        return list[0]?.id ?? null;
      });
    } catch (e) {
      setError(e?.message || "Failed to load practices");
      setPractices([]);
      setPracticeId(null);
    }
  }, [coachTeam]);

  const loadRoster = useCallback(async () => {
    try {
      const data = await apiFetch("/roster");
      const list = Array.isArray(data?.roster) ? data.roster : [];
      setRoster(list.filter((r) => (r.role_key || r.role || "").toLowerCase() === "athlete" && r.is_active !== false));
    } catch (e) {
      setError(e?.message || "Failed to load roster");
    }
  }, []);

  const loadBoatConfig = useCallback(async () => {
    try {
      const data = await apiFetch("/boat-config");
      setBoatConfig({
        hulls: data?.hulls || [],
        oars: data?.oars || [],
        coaches: data?.coaches || [],
      });
    } catch {
      // non-fatal; add-boat form just falls back to free text
    }
  }, []);

  const loadBoats = useCallback(async (pid) => {
    if (!pid) {
      setBoats([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`/boats/by-practice/${pid}${coachTeam ? `?team=${encodeURIComponent(coachTeam)}` : ""}`);
      setBoats(Array.isArray(data?.boats) ? data.boats : []);
    } catch (e) {
      setError(e?.message || "Failed to load boats");
    } finally {
      setLoading(false);
    }
  }, [coachTeam]);

  const loadPositionColors = useCallback(async () => {
    try {
      const positions = await apiFetch("/positions");
      const list = Array.isArray(positions) ? positions : [];
      const colorMap = {};
      list.forEach((p) => {
        if (p.name && p.color) colorMap[p.name.trim().toLowerCase()] = p.color;
      });
      setPositionColors(colorMap);
    } catch {
      // non-fatal; seats just render without a side color
    }
  }, []);

  const loadTeams = useCallback(async () => {
    try {
      const data = await apiFetch("/teams");
      setTeams(Array.isArray(data) ? data : []);
    } catch {
      // non-fatal; team select just falls back to the coach's own team
    }
  }, []);

  useEffect(() => { loadRoster(); loadBoatConfig(); loadPositionColors(); loadTeams(); }, [loadRoster, loadBoatConfig, loadPositionColors, loadTeams]);
  useEffect(() => { loadPractices(date); }, [date, loadPractices]);
  useEffect(() => { loadBoats(practiceId); }, [practiceId, loadBoats]);

  useEffect(() => {
    const normalizeStatus = (val) => {
      const s = typeof val === "string" ? val.toLowerCase() : "";
      if (s === "available" || s === "present") return "present";
      if (s === "not available" || s === "unavailable" || s === "absent") return "absent";
      return null;
    };
    const loadAttendance = async () => {
      if (!practiceId) {
        setAttendanceByUser({});
        return;
      }
      try {
        const rows = await apiFetch(`/attendance/by-practice/${practiceId}`);
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const id = row.userId ?? row.user_id ?? row.id;
          const st = normalizeStatus(row.status);
          if (id && st) map[id] = st;
        });
        setAttendanceByUser(map);
      } catch (e) {
        console.error("loadAttendance error:", e);
        setAttendanceByUser({});
      }
    };
    loadAttendance();
  }, [practiceId]);

  const teamScopedRoster = useMemo(() => {
    if (!teamFilter) return roster;
    return roster.filter((r) => String(r.team || "").trim().toUpperCase() === teamFilter.toUpperCase());
  }, [roster, teamFilter]);

  const rosterById = useMemo(() => {
    const m = new Map();
    roster.forEach((r) => m.set(String(r.id), r));
    return m;
  }, [roster]);

  const assignedUserIds = useMemo(() => {
    const s = new Set();
    boats.forEach((b) => (b.seats || []).forEach((seat) => {
      if (seat.userId) s.add(String(seat.userId));
    }));
    return s;
  }, [boats]);

  const squadFilterOptions = useMemo(() => {
    const set = new Set();
    teamScopedRoster.forEach((r) => {
      const value = r.squad ? String(r.squad).trim() : "";
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teamScopedRoster]);

  const positionFilterOptions = useMemo(() => {
    const set = new Set();
    teamScopedRoster.forEach((r) => {
      const value = r.position ? String(r.position).trim() : "";
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teamScopedRoster]);

  const activeFilterCount = [squadFilter !== "all", positionFilter !== "all", availFilter !== "all"].filter(Boolean).length;

  const unassigned = useMemo(() => {
    const q = search.trim().toLowerCase();
    return teamScopedRoster
      .filter((r) => !assignedUserIds.has(String(r.id)))
      .filter((r) => !q || (r.name || "").toLowerCase().includes(q))
      .filter((r) => squadFilter === "all" || String(r.squad || "").trim() === squadFilter)
      .filter((r) => positionFilter === "all" || String(r.position || "").trim() === positionFilter)
      .filter((r) => {
        if (availFilter === "all") return true;
        const st = attendanceByUser[r.id];
        if (availFilter === "available") return st === "present";
        if (availFilter === "unavailable") return st === "absent";
        if (availFilter === "noresponse") return st == null;
        return true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [teamScopedRoster, assignedUserIds, search, squadFilter, positionFilter, availFilter, attendanceByUser]);

  async function addBoat() {
    if (!canEdit || !practiceId) return;
    if (!teamFilter) {
      setError("Pick a team before adding a boat.");
      return;
    }
    try {
      const created = await apiFetch(`/boats/${practiceId}`, {
        method: "POST",
        body: JSON.stringify({ type: addType, team: teamFilter }),
      });
      setBoats((prev) => [
        ...prev,
        {
          id: created.id,
          type: addType,
          name: "",
          hull: "",
          coach: "",
          oars: "",
          coxBox: "",
          speedCoach: "",
          launch: "",
          team: teamFilter,
          squad: "",
          seats: seatsFor(addType).map((seat) => ({ seat, userId: null, guestName: null })),
        },
      ]);
    } catch (e) {
      setError(e?.message || "Failed to add boat");
    }
  }

  async function removeBoat(boatId) {
    if (!canEdit || !practiceId) return;
    if (!window.confirm("Remove this boat?")) return;
    try {
      await apiFetch(`/boats/${practiceId}/${boatId}`, { method: "DELETE" });
      setBoats((prev) => prev.filter((b) => b.id !== boatId));
    } catch (e) {
      setError(e?.message || "Failed to remove boat");
    }
  }

  async function updateBoatField(boatId, field, value) {
    setBoats((prev) => prev.map((b) => (b.id === boatId ? { ...b, [field]: value } : b)));
    if (!canEdit || !practiceId) return;
    try {
      await apiFetch(`/boats/${practiceId}/${boatId}`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value }),
      });
    } catch (e) {
      setError(e?.message || "Failed to save boat");
    }
  }

  async function assignSeat(boatId, seat, userId, guestName) {
    if (!canEdit || !practiceId) return;
    setBoats((prev) =>
      prev.map((b) => {
        if (b.id !== boatId) return b;
        const seats = (b.seats || []).map((s) =>
          s.seat === seat ? { seat, userId: userId || null, guestName: guestName || null } : s
        );
        return { ...b, seats };
      })
    );
    setPicker(null);
    try {
      await apiFetch(`/boats/${practiceId}/${boatId}/seat`, {
        method: "POST",
        body: JSON.stringify({ seat, userId: userId || null, guestName: guestName || undefined }),
      });
    } catch (e) {
      setError(e?.message || "Failed to assign seat");
    }
  }

  async function moveSeat(fromBoatId, fromSeat, toBoatId, toSeat) {
    if (!canEdit || !practiceId) return;
    if (fromBoatId === toBoatId && fromSeat === toSeat) return;

    let fromOccupant = null;
    let toOccupant = null;
    setBoats((prev) => {
      const fromBoat = prev.find((b) => b.id === fromBoatId);
      fromOccupant = (fromBoat?.seats || []).find((s) => s.seat === fromSeat) || null;
      const toBoat = prev.find((b) => b.id === toBoatId);
      toOccupant = (toBoat?.seats || []).find((s) => s.seat === toSeat) || null;

      return prev.map((b) => {
        if (b.id === fromBoatId && b.id === toBoatId) {
          const seats = (b.seats || []).map((s) => {
            if (s.seat === fromSeat) return { seat: fromSeat, userId: toOccupant?.userId || null, guestName: toOccupant?.guestName || null };
            if (s.seat === toSeat) return { seat: toSeat, userId: fromOccupant?.userId || null, guestName: fromOccupant?.guestName || null };
            return s;
          });
          return { ...b, seats };
        }
        if (b.id === fromBoatId) {
          const seats = (b.seats || []).map((s) =>
            s.seat === fromSeat ? { seat: fromSeat, userId: toOccupant?.userId || null, guestName: toOccupant?.guestName || null } : s
          );
          return { ...b, seats };
        }
        if (b.id === toBoatId) {
          const seats = (b.seats || []).map((s) =>
            s.seat === toSeat ? { seat: toSeat, userId: fromOccupant?.userId || null, guestName: fromOccupant?.guestName || null } : s
          );
          return { ...b, seats };
        }
        return b;
      });
    });

    try {
      await apiFetch(`/boats/${practiceId}/${fromBoatId}/seat`, {
        method: "POST",
        body: JSON.stringify({ seat: fromSeat, userId: toOccupant?.userId || null, guestName: toOccupant?.guestName || undefined }),
      });
      await apiFetch(`/boats/${practiceId}/${toBoatId}/seat`, {
        method: "POST",
        body: JSON.stringify({ seat: toSeat, userId: fromOccupant?.userId || null, guestName: fromOccupant?.guestName || undefined }),
      });
    } catch (e) {
      setError(e?.message || "Failed to move seat");
    }
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="card" style={{ padding: 12 }}>
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <select
            className="input"
            style={{ width: "auto" }}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <option value="">Select team…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.name}>{t.name}</option>
            ))}
          </select>
          {practices.length > 1 && (
            <select
              className="input"
              style={{ width: "auto" }}
              value={practiceId || ""}
              onChange={(e) => setPracticeId(e.target.value)}
            >
              {practices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.abbrevName || "Practice"} {p.startTime ? `(${p.startTime})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        {!teamFilter && (
          <p className="help" style={{ marginTop: 8 }}>Select a team to see and add boats.</p>
        )}
        {error && <p className="help" style={{ color: "red", marginTop: 8 }}>{error}</p>}
        {!canEdit && (
          <p className="help" style={{ marginTop: 8 }}>View only — you don't have lineup edit permission.</p>
        )}
      </div>

      {!practiceId ? (
        <div className="card" style={{ padding: 16 }}>No practice scheduled for this date.</div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {/* Athlete side panel */}
          {athletePanelCollapsed ? (
            <div className="lineup-left-rail">
              <button
                type="button"
                className="lineup-panel-tab"
                onClick={() => setAthletePanelCollapsed(false)}
                aria-label="Show athletes"
                title="Show athletes"
              >
                <span>Athletes</span>
              </button>
            </div>
          ) : (
            <div className="card" style={{ padding: 10, width: 200, flexShrink: 0, position: "sticky", top: 8 }}>
              <div className="colHeader" style={{ marginBottom: 8 }}>
                <strong>Athletes</strong>
                <button
                  type="button"
                  className="lineup-panel-toggle"
                  onClick={() => setAthletePanelCollapsed(true)}
                  aria-label="Collapse athletes panel"
                  title="Collapse"
                >
                  <FaChevronLeft />
                </button>
              </div>
              <div className="row" style={{ gap: 6, marginBottom: 8 }}>
                <input
                  className="input"
                  placeholder="Search athletes…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  className={`btn-filter icon-only ${showFilters ? "active" : ""}`}
                  onClick={() => setShowFilters((v) => !v)}
                  title="Filters"
                  aria-label="Filters"
                >
                  <FaFilter />
                  {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
                </button>
              </div>
              {showFilters && (
                <div className="filterBarPanel" style={{ marginBottom: 8 }}>
                  <select className="input filterSelect" value={squadFilter} onChange={(e) => setSquadFilter(e.target.value)}>
                    <option value="all">All Squads</option>
                    {squadFilterOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <select className="input filterSelect" value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
                    <option value="all">All Positions</option>
                    {positionFilterOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <select className="input filterSelect" value={availFilter} onChange={(e) => setAvailFilter(e.target.value)}>
                    <option value="all">All Availability</option>
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="noresponse">No Response</option>
                  </select>
                  <button
                    type="button"
                    className="btn-clear-filters"
                    onClick={() => {
                      setSquadFilter("all");
                      setPositionFilter("all");
                      setAvailFilter("all");
                    }}
                  >
                    Clear all filters
                  </button>
                </div>
              )}
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
                Unassigned ({unassigned.length})
              </div>
              {isTouchDevice && canEdit && (
                <p className="help" style={{ fontSize: 11, marginTop: -2, marginBottom: 6 }}>
                  {selectedAthleteId ? "Now tap a seat to place them." : "Tap an athlete, then tap a seat."}
                </p>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: "70vh", overflowY: "auto" }}>
                {unassigned.map((a) => {
                  const color = getPositionColor(a.position, positionColors);
                  const isSelected = selectedAthleteId === a.id;
                  return (
                    <div
                      key={a.id}
                      draggable={canEdit && !isTouchDevice}
                      onDragStart={(e) => e.dataTransfer.setData("text/lean-user-id", String(a.id))}
                      onClick={() => {
                        if (!canEdit || !isTouchDevice) return;
                        setSelectedAthleteId((prev) => (prev === a.id ? null : a.id));
                      }}
                      style={{
                        padding: "5px 8px",
                        borderRadius: 6,
                        background: color || "var(--bg-elevated)",
                        border: `${isSelected ? 2 : 1}px solid ${isSelected ? "var(--primary)" : (color || "var(--border)")}`,
                        fontSize: 13,
                        cursor: canEdit ? (isTouchDevice ? "pointer" : "grab") : "default",
                      }}
                      title={a.position || a.squadName || ""}
                    >
                      {a.name}
                    </div>
                  );
                })}
                {unassigned.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>Everyone's assigned.</div>
                )}
              </div>
            </div>
          )}

          {/* Boat grid */}
          <div style={{ flex: 1, overflowX: "auto" }}>
            {canEdit && (
              <div className="row" style={{ gap: 8, marginBottom: 8 }}>
                <select className="input" style={{ width: "auto" }} value={addType} onChange={(e) => setAddType(e.target.value)}>
                  <option value="eight">8+</option>
                  <option value="four">4+</option>
                  <option value="pair">2-</option>
                </select>
                <button className="btn small" onClick={addBoat} disabled={!teamFilter}>+ Add Boat</button>
              </div>
            )}

            {loading ? (
              <div className="card" style={{ padding: 16 }}>Loading boats…</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
                {boats.map((boat) => (
                  <BoatColumn
                    key={boat.id}
                    boat={boat}
                    canEdit={canEdit}
                    rosterById={rosterById}
                    boatConfig={boatConfig}
                    positionColors={positionColors}
                    onField={(field, value) => updateBoatField(boat.id, field, value)}
                    onRemove={() => removeBoat(boat.id)}
                    onOpenPicker={(seat) => {
                      if (selectedAthleteId) {
                        assignSeat(boat.id, seat, selectedAthleteId, null);
                        setSelectedAthleteId(null);
                      } else {
                        setPicker({ boatId: boat.id, seat });
                      }
                    }}
                    onDropAssign={(seat, userId) => assignSeat(boat.id, seat, userId, null)}
                    onMoveSeat={(fromBoatId, fromSeat, seat) => moveSeat(fromBoatId, fromSeat, boat.id, seat)}
                  />
                ))}
                {boats.length === 0 && (
                  <div className="card" style={{ padding: 16 }}>No boats yet for this practice.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {picker && (
        <SeatPickerModal
          roster={teamScopedRoster}
          onClose={() => setPicker(null)}
          onPick={(userId, guestName) => assignSeat(picker.boatId, picker.seat, userId, guestName)}
        />
      )}
    </div>
  );
}

function getPositionColor(position, colorMap) {
  if (!position || !colorMap) return null;
  const key = String(position).trim().toLowerCase();
  if (colorMap[key]) return colorMap[key];
  for (const [name, color] of Object.entries(colorMap)) {
    if (key.includes(name) || name.includes(key)) return color;
  }
  return null;
}

function BoatColumn({ boat, canEdit, rosterById, boatConfig, positionColors, onField, onRemove, onOpenPicker, onDropAssign, onMoveSeat }) {
  const seats = seatsFor(boat.type);
  const seatMap = useMemo(() => {
    const m = new Map();
    (boat.seats || []).forEach((s) => m.set(s.seat, s));
    return m;
  }, [boat.seats]);

  return (
    <div
      className="card"
      style={{ padding: 8, width: 150, flexShrink: 0 }}
    >
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>{BOAT_TYPE_LABEL[boat.type] || boat.type}</span>
        {canEdit && (
          <button
            className="btn tiny ghost"
            onClick={onRemove}
            title="Remove boat"
            style={{ padding: "0 4px", lineHeight: 1 }}
          >
            ×
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <BoatHeaderField
          value={boat.coach}
          placeholder="Coach"
          canEdit={canEdit}
          onChange={(v) => onField("coach", v)}
          list={boatConfig.coaches}
        />
        <BoatHeaderField
          value={boat.hull}
          placeholder="Hull"
          canEdit={canEdit}
          onChange={(v) => onField("hull", v)}
          list={(boatConfig.hulls || []).map((h) => h.value)}
        />
        <BoatHeaderField
          value={boat.oars}
          placeholder="Oars"
          canEdit={canEdit}
          onChange={(v) => onField("oars", v)}
          list={(boatConfig.oars || []).map((o) => o.value)}
        />
      </div>

      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
        {seats.map((seat) => {
          const s = seatMap.get(seat);
          const athlete = s?.userId ? rosterById.get(String(s.userId)) : null;
          const label = s?.guestName || athlete?.name || "";
          const sideColor = athlete ? getPositionColor(athlete.position, positionColors) : null;
          const isOccupied = Boolean(s?.userId || s?.guestName);
          return (
            <div
              key={seat}
              draggable={canEdit && isOccupied}
              onDragStart={(e) => {
                if (!canEdit || !isOccupied) return;
                e.dataTransfer.setData("text/lean-seat-move", JSON.stringify({ boatId: boat.id, seat }));
              }}
              onClick={() => canEdit && onOpenPicker(seat)}
              onDragOver={(e) => canEdit && e.preventDefault()}
              onDrop={(e) => {
                if (!canEdit) return;
                const seatMoveRaw = e.dataTransfer.getData("text/lean-seat-move");
                if (seatMoveRaw) {
                  try {
                    const { boatId: fromBoatId, seat: fromSeat } = JSON.parse(seatMoveRaw);
                    if (fromBoatId != null && fromSeat != null) onMoveSeat(fromBoatId, fromSeat, seat);
                  } catch {}
                  return;
                }
                const uid = e.dataTransfer.getData("text/lean-user-id");
                if (uid) onDropAssign(seat, uid);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 6px",
                borderRadius: 5,
                background: sideColor || (seat === "C" ? "var(--bg-hover)" : "var(--bg-elevated)"),
                border: `1px solid ${sideColor || "var(--border)"}`,
                fontSize: 12,
                cursor: canEdit ? (isOccupied ? "grab" : "pointer") : "default",
                minHeight: 20,
              }}
              title={athlete ? [athlete.name, athlete.position].filter(Boolean).join(" • ") : ""}
            >
              <span style={{ width: 16, fontWeight: 700, color: "var(--muted)", flexShrink: 0 }}>{seat}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label || (canEdit ? "—" : "")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BoatHeaderField({ value, placeholder, canEdit, onChange, list }) {
  const listId = useRef(`dl-${Math.random().toString(36).slice(2)}`).current;
  const abbrev = placeholder.slice(0, 1);
  if (!canEdit) {
    return (
      <div
        style={{ fontSize: 10, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={value || placeholder}
      >
        <span style={{ fontWeight: 700 }}>{abbrev}</span> {value || ""}
      </div>
    );
  }
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", width: 8, flexShrink: 0 }}>{abbrev}</span>
        <input
          className="input"
          style={{ flex: 1, minWidth: 0, fontSize: 10, padding: "1px 4px", height: 17, borderRadius: 4 }}
          value={value || ""}
          title={placeholder}
          list={listId}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {list && list.length > 0 && (
        <datalist id={listId}>
          {list.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}
    </>
  );
}

function SeatPickerModal({ roster, onClose, onPick }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return roster
      .filter((r) => !query || (r.name || "").toLowerCase().includes(query))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .slice(0, 50);
  }, [roster, q]);

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalCard" style={{ maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="h2" style={{ marginTop: 0 }}>Assign Seat</h3>
        <input
          className="input"
          autoFocus
          placeholder="Search athlete or type guest name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: "100%", marginBottom: 8 }}
        />
        <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          <button className="btn ghost small" onClick={() => onPick(null, null)}>
            Clear seat
          </button>
          {q.trim() && (
            <button className="btn ghost small" onClick={() => onPick(null, q.trim())}>
              Add "{q.trim()}" as guest
            </button>
          )}
          {filtered.map((a) => (
            <button
              key={a.id}
              className="btn ghost small"
              style={{ justifyContent: "flex-start" }}
              onClick={() => onPick(a.id, null)}
            >
              {a.name}
              {a.squadName ? <span style={{ color: "var(--muted)", marginLeft: 6, fontSize: 11 }}>{a.squadName}</span> : null}
            </button>
          ))}
        </div>
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn ghost small" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
